import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./LandingV8.css";

const A = "/assets/";

const ASSETS = {
  abhimanyu: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062464/comicmenia/landing-assets/abhimanyu.png",
  flameHero: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062496/comicmenia/landing-assets/flame-hero-crop.png",
  vishnu: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062483/comicmenia/landing-assets/vishnu.png",
  flameExpressions: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062436/comicmenia/landing-assets/flame-expressions-crop.png",
  forestScenes: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062531/comicmenia/landing-assets/forest-scenes-crop.png",
  flameCharacters: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062606/comicmenia/landing-assets/flame-characters-crop.png",
  forestHero: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062526/comicmenia/landing-assets/forest-hero-crop.png",
  flameBible: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062540/comicmenia/landing-assets/flame-bible-top.png",
  flowStrip: "https://res.cloudinary.com/dwjabtie6/image/upload/v1788062421/comicmenia/landing-assets/flow-strip.png",
};

const getImageSrc = (src) => {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  return `${A}${src}`;
};

const HERO_MAIN_IMG = ASSETS.abhimanyu;

const heroFrames = [
  { src: ASSETS.flameHero, label: "CHARACTER", cls: "hero-f1" },
  { src: ASSETS.vishnu, label: "WORLD · 02", cls: "hero-f2" },
  { src: ASSETS.flameExpressions, label: "CHARACTER LAB", cls: "hero-f3" },
  { src: ASSETS.forestScenes, label: "ENVIRONMENT", cls: "hero-f4" },
];

const scrollFrames = [
  { src: ASSETS.abhimanyu, label: "HERO · 01" },
  { src: ASSETS.vishnu, label: "WORLD · 02" },
  { src: ASSETS.flameCharacters, label: "CHARACTER LAB" },
  { src: ASSETS.forestHero, label: "ENVIRONMENT" },
  { src: ASSETS.flameBible, label: "CHARACTER BIBLE" },
];

const fallbackGallery = [
  { id: 'fb-1', src: ASSETS.flameBible, label: "CHARACTER BIBLE", title: "BUILD THE CAST.", description: "Reusable character references for recurring heroes and worlds." },
  { id: 'fb-2', src: ASSETS.vishnu, label: "MYTHIC WORLDS", title: "MAKE IT EPIC.", description: "Visual worldbuilding layouts and environmental concepts." },
  { id: 'fb-3', src: ASSETS.flameExpressions, label: "CHARACTER LAB", title: "MAKE THEM ALIVE.", description: "Expression sheets, poses, and character variations." },
  { id: 'fb-4', src: ASSETS.forestScenes, label: "ENVIRONMENT DESIGN", title: "MAKE IT FEEL REAL.", description: "Atmospheric landscapes and background panel art." },
  { id: 'fb-5', src: ASSETS.abhimanyu, label: "HERO DESIGN", title: "UNFORGETTABLE CREATIONS.", description: "High-definition hero character concept art." },
  { id: 'fb-6', src: ASSETS.flameCharacters, label: "PANEL SEQUENCES", title: "DYNAMIC STORYBOARDS.", description: "Sequential comic layouts and panel compositions." },
];

function ImageFrame({ src, alt, className = "", label }) {
  return (
    <div className={`cm-frame ${className}`}>
      <img src={getImageSrc(src)} alt={alt} />
      {label && <span className="cm-frame-label">{label}</span>}
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const frameRefs = useRef([]);
  const [creatorProjects, setCreatorProjects] = useState([]);
  const [selectedModalProject, setSelectedModalProject] = useState(null);

  // Reveal animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = document.querySelectorAll(".cm-reveal");
    reveals.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
    axios
      .get(`${backendUrl}/api/projects/public-latest?limit=5`)
      .then((response) => {
        const items = response.data?.projects || response.data?.items || response.data || [];
        if (items.length > 0) {
          const mapped = items.slice(0, 5).map((item) => ({
            id: item.id,
            thumbnail: item.thumbnail || item.image_url,
            label: item.author_name || item.author_username ? `BY @${(item.author_username || item.author_name).toUpperCase()}` : "CREATOR PROJECT",
            title: item.title || "UNTITLED ARTWORK",
            description: item.description || "",
            author: item.author_name || item.author_username || "Creator",
            created_at: item.created_at,
            width: item.width,
            height: item.height,
            isRealProject: true,
          }));
          setCreatorProjects(mapped);
        } else {
          // No public projects — show fallback showcase images (not openable in editor)
          setCreatorProjects(fallbackGallery.slice(0, 5));
        }
      })
      .catch((error) => {
        console.error("Failed to load public projects:", error);
        setCreatorProjects(fallbackGallery.slice(0, 5));
      });
  }, []);

  // Parallax scroll effect for hero frames
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const hero = heroRef.current;
      if (!hero) return;

      const rect = hero.getBoundingClientRect();
      const p = Math.max(-0.4, Math.min(1.2, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      const move = p - 0.35;

      const transforms = [
        `translate(${move * -42}px,${move * 22}px) rotate(-5deg)`,
        `translate(${move * 48}px,${move * 17}px) rotate(5deg)`,
        `translate(${move * -58}px,${move * -18}px) rotate(3deg)`,
        `translate(${move * 62}px,${move * -25}px) rotate(-5deg)`,
      ];

      frameRefs.current.forEach((node, i) => {
        if (node) node.style.transform = transforms[i];
      });
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="cm-page">
      <main id="top">
        <section className="cm-hero cm-container">
          <div className="cm-hero-copy cm-reveal">
            <div className="cm-issue">ISSUE 01 <span>•</span> THE CREATOR ERA</div>
            <h1 className="cm-hero-title">
              MAKE<br />
              <span className="cm-outline">COMICS.</span><br />
              <span className="cm-gradient">BUILD WORLDS.</span>
            </h1>
            <p>
              ComicMenia is a new kind of comic platform — part AI studio,
              part creator community, part universe engine. Start with an idea.
              Leave with a world.
            </p>
            <div className="cm-actions">
              <a className="cm-btn cm-primary" href="/comica">🚀 START CREATING</a>
              <Link className="cm-btn" to="/comics">READ COMICS ↑</Link>
            </div>
            <div className="cm-proof">
              <span className="cm-dots"><i /><i /><i /></span>
              <span>FOR WRITERS · ARTISTS · WORLD BUILDERS</span>
            </div>
          </div>

          <div className="cm-hero-art cm-reveal" ref={heroRef}>
            <div className="cm-hero-glow" />
            <ImageFrame
              src={HERO_MAIN_IMG}
              alt="Hero artwork"
              label="HERO · 01"
              className="cm-main-frame"
            />
            {heroFrames.map((frame, i) => (
              <div
                key={frame.src}
                ref={(node) => (frameRefs.current[i] = node)}
                className={`cm-frame ${frame.cls}`}
              >
                <img src={getImageSrc(frame.src)} alt={frame.label} />
                <span className="cm-frame-label">{frame.label}</span>
              </div>
            ))}
            <div className="cm-bubble">YOUR IDEA<br />JUST GOT<br />LOUDER.</div>
            <div className="cm-pow">POW!</div>
            <div className="cm-hero-note">SCROLL TO EXPLORE ↓</div>
          </div>
        </section>

        <section className="cm-section" id="stories">
          <div className="cm-container">
            <div className="cm-head cm-reveal">
              <div>
                <div className="cm-eyebrow">01 · THE COMIC WORKFLOW</div>
                <h2 className="cm-title">FROM BLANK<br />PAGE TO <em>UNIVERSE.</em></h2>
              </div>
              <p className="cm-copy">
                Great comic tools don't just generate images. They help you think in
                characters, scenes, panels, pages and worlds — then keep everything connected.
              </p>
            </div>
            <div className="cm-workflow cm-reveal">
              <img src={ASSETS.flowStrip} alt="ComicMenia workflow" />
            </div>
          </div>
        </section>

        <section className="cm-section" id="studio">
          <div className="cm-container cm-product">
            <div className="cm-product-window cm-reveal">
              <div className="cm-windowbar"><i /><i /><i /><span /></div>
              <div className="cm-canvas">
                <div className="cm-canvas-page">
                  {scrollFrames.map((f, idx) => (
                    <div className="cm-canvas-panel" key={idx}>
                      <img src={getImageSrc(f.src)} alt="" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="cm-reveal">
              <div className="cm-eyebrow">02 · THE CREATOR STUDIO</div>
              <h2 className="cm-title">YOUR STORY.<br />YOUR <em>RULES.</em></h2>
              <p className="cm-copy">
                A visual workspace designed around the way comics are actually made —
                not another generic image generator with a comic filter.
              </p>
              <div className="cm-steps">
                <div className="cm-step active"><b>01</b><div><small>GENERATE</small><h3>Make the panel.</h3><p>Start with a scene and iterate until the visual moment feels right.</p></div></div>
                <div className="cm-step"><b>02</b><div><small>CONSISTENCY</small><h3>Keep your cast alive.</h3><p>Build reusable character references for recurring heroes and worlds.</p></div></div>
                <div className="cm-step"><b>03</b><div><small>COMPOSE</small><h3>Build the page.</h3><p>Move from isolated images into panels, pacing, dialogue and complete comics.</p></div></div>
              </div>
            </div>
          </div>
        </section>

        <section className="cm-section" id="characters">
          <div className="cm-container">
            <div className="cm-head cm-reveal">
              <div>
                <div className="cm-eyebrow">03 · CHARACTER + WORLD BUILDING</div>
                <h2 className="cm-title">A HERO ISN'T<br />JUST A <em>FACE.</em></h2>
              </div>
              <p className="cm-copy">Give characters a visual identity. Give worlds a visual language. Then let your stories grow inside them.</p>
            </div>

            <div className="cm-gallery cm-reveal">
              {(creatorProjects.length > 0 ? creatorProjects : fallbackGallery.slice(0, 5)).map((project, index) => {
                const image = project.thumbnail || getImageSrc(project.src);
                const label = project.label || "COMICMENIA PROJECT";
                const title = project.title || project.name || "UNTITLED PROJECT";
                const cardClass = index === 0 ? "big" : index === 3 ? "wide" : "";
                return (
                  <article
                    className={`cm-card ${cardClass}`}
                    key={project.id || index}
                    onClick={() => {
                      if (project.isRealProject) {
                        // Real public project — open directly in Comica Studio read-only
                        navigate('/comica', { state: { project, readOnly: true } });
                      } else {
                        // Fallback showcase — just show preview modal
                        setSelectedModalProject(project);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                    title={project.isRealProject ? `Open "${title}" in Read-Only Studio` : "Click to preview"}
                  >
                    <img src={image} alt={title} />
                    <div><small>{label}</small><h3>{title}</h3></div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="cm-section" id="community">
          <div className="cm-container">
            <div className="cm-head cm-reveal">
              <div><div className="cm-eyebrow">04 · THE ECOSYSTEM</div><h2 className="cm-title">BUILD THE<br /><em>UNIVERSE.</em></h2></div>
              <p className="cm-copy">ComicMenia connects creation with discovery — so the story doesn't end when the page is finished.</p>
            </div>
            <div className="cm-eco cm-reveal">
              {[
                ["01", "Publish", "Turn finished pages into comics readers can discover, follow and share.", "EXPLORE STORIES →"],
                ["02", "Connect", "Find creators, discuss ideas, collaborate and grow a community around your interests.", "MEET CREATORS →"],
                ["03", "Monetize", "Extend a universe beyond the page with merchandise, collectibles and future experiences.", "MARKETPLACE →"],
              ].map(([n, title, text, link]) => (
                <article className="cm-eco-card" key={n}><b>{n}</b><h3>{title}</h3><p>{text}</p><a href="#top">{link}</a></article>
              ))}
            </div>
          </div>
        </section>

        <section className="cm-final" id="pricing">
          <div className="cm-container cm-reveal">
            <div className="cm-eyebrow">05 · ISSUE CLOSED. STORY OPEN.</div>
            <h2>YOUR NEXT<br /><em>COMIC STARTS NOW.</em></h2>
            <p>The next great universe doesn't need permission. It needs a place to start.</p>
            <Link className="cm-btn cm-primary" to="/comica">🚀 LAUNCH COMICA</Link>
          </div>
        </section>
      </main>

      {/* Read-Only Project View Modal */}
      {selectedModalProject && (
        <div className="cm-modal-backdrop" onClick={() => setSelectedModalProject(null)}>
          <div className="cm-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="cm-modal-close" onClick={() => setSelectedModalProject(null)}>✕</button>
            <div className="cm-modal-header">
              <span className="cm-modal-badge">
                {selectedModalProject.isRealProject ? '👁 READ-ONLY PROJECT FILE' : '🎨 SHOWCASE PREVIEW'}
              </span>
              <h2>{selectedModalProject.title || selectedModalProject.name || "Untitled Artwork"}</h2>
              {selectedModalProject.label && <p className="cm-modal-author">{selectedModalProject.label}</p>}
            </div>
            <div className="cm-modal-body">
              <div className="cm-modal-img-wrap">
                <img
                  src={selectedModalProject.thumbnail || getImageSrc(selectedModalProject.src)}
                  alt={selectedModalProject.title}
                />
              </div>
              {selectedModalProject.description && (
                <p className="cm-modal-desc">{selectedModalProject.description}</p>
              )}
              <div className="cm-modal-meta">
                {selectedModalProject.created_at && (
                  <span>Published: {new Date(selectedModalProject.created_at).toLocaleDateString()}</span>
                )}
                <span>Status: Public Read-Only Blueprint</span>
              </div>
            </div>
            <div className="cm-modal-footer">
              <button className="cm-btn" onClick={() => setSelectedModalProject(null)}>Close</button>
              {selectedModalProject.isRealProject ? (
                <button
                  className="cm-btn cm-primary"
                  onClick={() => {
                    const proj = selectedModalProject;
                    setSelectedModalProject(null);
                    navigate('/comica', { state: { project: proj, readOnly: true } });
                  }}
                >
                  🎨 Open Full Blueprint Studio
                </button>
              ) : (
                <button
                  className="cm-btn cm-primary"
                  onClick={() => {
                    setSelectedModalProject(null);
                    navigate('/comica');
                  }}
                >
                  🚀 LAUNCH COMICA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
